import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Navigation, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/site/primitives";
import { CONTACT_INFO, CONTACT_HOURS, CONTACT_SUBJECTS } from "@/content/contact";

const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT_INFO.mapQuery)}`;
const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(CONTACT_INFO.mapQuery)}&output=embed`;

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: CONTACT_SUBJECTS[0], message: "" });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Please enter a valid email.";
    if (!form.message.trim()) next.message = "Please enter a message.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSubmitting(true);
    try {
      await api.post("/forms/contact", { ...form, email: form.email.trim() });
      toast.success("Thanks for reaching out!", { description: "We received your message and a member of our staff will be in touch soon." });
      setForm({ name: "", email: "", subject: CONTACT_SUBJECTS[0], message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("We couldn't send your message", { description: typeof detail === "string" ? detail : "Please try again, or email staff@worldfarmers.org directly." });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "min-h-11 w-full rounded-lg border border-[var(--wf-border)] bg-[var(--wf-paper)] px-3.5 py-2.5 text-sm text-[var(--wf-ink)] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <section id="contact" aria-labelledby="contact-heading" className="wf-section bg-white">
      <div className="wf-container">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          {/* Form */}
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">Send a message</span>
            <h2 id="contact-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">We would love to hear from you</h2>
            <form onSubmit={submit} noValidate className="mt-8 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="c-name" className="mb-1.5 block text-sm font-medium text-[var(--wf-ink)]">Name</label>
                  <input id="c-name" type="text" value={form.name} onChange={set("name")} data-testid="contact-name-input" className={inputCls} placeholder="Your name" aria-invalid={!!errors.name} />
                  {errors.name && <p className="mt-1 text-xs text-[var(--wf-maroon)]" data-testid="contact-name-error">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="c-email" className="mb-1.5 block text-sm font-medium text-[var(--wf-ink)]">Email</label>
                  <input id="c-email" type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className={inputCls} placeholder="you@example.com" aria-invalid={!!errors.email} />
                  {errors.email && <p className="mt-1 text-xs text-[var(--wf-maroon)]" data-testid="contact-email-error">{errors.email}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="c-subject" className="mb-1.5 block text-sm font-medium text-[var(--wf-ink)]">Subject</label>
                <Select value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}>
                  <SelectTrigger id="c-subject" className="min-h-11 border-[var(--wf-border)] bg-[var(--wf-paper)]" data-testid="contact-subject-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTACT_SUBJECTS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="c-message" className="mb-1.5 block text-sm font-medium text-[var(--wf-ink)]">Message</label>
                <textarea id="c-message" rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" className={`${inputCls} resize-y`} placeholder="How can we help?" aria-invalid={!!errors.message} />
                {errors.message && <p className="mt-1 text-xs text-[var(--wf-maroon)]" data-testid="contact-message-error">{errors.message}</p>}
              </div>
              <button type="submit" disabled={submitting} aria-busy={submitting} data-testid="contact-submit-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--wf-maroon)] px-6 py-3.5 text-sm font-semibold text-[var(--wf-paper)] shadow-[0_10px_28px_rgba(107,31,43,0.28)] transition-colors duration-200 hover:bg-[var(--wf-maroon-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background">
                <Send className="h-4 w-4" aria-hidden="true" /> Send message
              </button>
              <p className="text-xs text-muted-foreground">Messages go straight to our staff inbox. We typically reply within two business days.</p>
            </form>
          </Reveal>

          {/* Visit us */}
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">Visit us</span>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">Find the farm</h2>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <a href={CONTACT_INFO.phoneHref} data-testid="contact-phone-link" className="flex items-start gap-3 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-4 transition-colors hover:border-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Phone className="mt-0.5 h-5 w-5 text-[var(--wf-maroon)]" aria-hidden="true" />
                <span><span className="block text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Phone</span><span className="text-sm font-medium text-[var(--wf-ink)]">{CONTACT_INFO.phone}</span></span>
              </a>
              <a href={CONTACT_INFO.emailHref} data-testid="contact-email-link" className="flex items-start gap-3 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-4 transition-colors hover:border-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Mail className="mt-0.5 h-5 w-5 text-[var(--wf-maroon)]" aria-hidden="true" />
                <span><span className="block text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Email</span><span className="text-sm font-medium text-[var(--wf-ink)]">{CONTACT_INFO.email}</span></span>
              </a>
              <div className="flex items-start gap-3 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-4">
                <MapPin className="mt-0.5 h-5 w-5 text-[var(--wf-maroon)]" aria-hidden="true" />
                <span><span className="block text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{CONTACT_INFO.officeLabel}</span>{CONTACT_INFO.addressLines.map((l) => (<span key={l} className="block text-sm font-medium text-[var(--wf-ink)]">{l}</span>))}</span>
              </div>
              <div className="flex items-start gap-3 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-4">
                <Clock className="mt-0.5 h-5 w-5 text-[var(--wf-maroon)]" aria-hidden="true" />
                <span><span className="block text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Hours</span>{CONTACT_HOURS.map((h) => (<span key={h.label} className="block text-sm font-medium text-[var(--wf-ink)]">{h.label}: {h.value}</span>))}</span>
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-sm border border-[var(--wf-border)]">
              <iframe title="Map to World Farmers" src={mapEmbed} data-testid="contact-map" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-64 w-full border-0" />
            </div>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-directions-link" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--wf-farm-green)] px-5 py-3 text-sm font-semibold text-white transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background">
              <Navigation className="h-4 w-4" aria-hidden="true" /> Get directions
            </a>

            <div className="mt-6 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-4" data-testid="contact-mailing-address">
              <span className="block text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Send us a letter</span>
              <span className="mt-1 block text-sm text-[var(--wf-soil)]">Our mailing address:</span>
              {CONTACT_INFO.mailingLines.map((l) => (
                <span key={l} className="block text-sm font-medium text-[var(--wf-ink)]">{l}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
